import { makeAutoObservable, runInAction } from 'mobx'
import { uniq } from 'lodash'
import { errorHandler } from 'shared/api'
import { getContentType, getFileWithTruncatedName, logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { AttachmentsApi } from 'entities/Attachment/api/attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import type { ILimitSizeError, IUploadInfoItem, IUploadInfoKeys } from 'entities/Attachment'

import { getFileType } from '../lib/getFileType'
import { fileTypeToLabelConverter } from '../lib/fileTypeToLabelConverter'

export class AttachmentStore {
  constructor() {
    makeAutoObservable(this)
  }
  previousAttachmentMap: Map<string, Attachment | null> = new Map()
  uploadInfoMap: Map<IUploadInfoKeys, IUploadInfoItem> = new Map()
  loadingUploadInfo = false

  get allFilesAccept() {
    return Array.from(this.uploadInfoMap.values()).reduce<string[]>((acc, { mime_types }) => {
      return [...acc, ...mime_types]
    }, [])
  }

  get imageAccept() {
    return this.getAllowedMimeTypes('image')
  }

  get fileAccept() {
    return this.getAllowedMimeTypes('docs')
  }

  get videoAccept() {
    return this.getAllowedMimeTypes('video')
  }

  get audioAccept() {
    return this.getAllowedMimeTypes('audio')
  }

  get vCardAccept() {
    return this.getAllowedMimeTypes('vcard')
  }

  get extendedFilesAccept() {
    return [...this.fileAccept, 'text/markdown', 'text/plain', 'text/html']
  }

  // Accept strings
  get allFilesAcceptString() {
    return this.allFilesAccept.toString()
  }

  get fileAcceptString() {
    return this.fileAccept.toString()
  }

  get imageAcceptString() {
    return this.imageAccept.toString()
  }

  get videoAcceptString() {
    return this.videoAccept.toString()
  }

  get audioAcceptString() {
    return this.audioAccept.toString()
  }

  get vCardAcceptString() {
    return this.vCardAccept.toString()
  }

  get extendedFilesAcceptString() {
    return this.extendedFilesAccept.toString()
  }

  private normalizeShortUrl(url: string): string {
    const match = url.match(/^(.+\/)([^/]+)$/)

    if (match) {
      const [, baseUrl, lastSegment] = match
      const normalizedSegment = lastSegment.replace(/-[a-zA-Z0-9]+$/, '')
      return baseUrl + normalizedSegment
    }

    return url
  }

  getAttachmentByLink(link: string): Attachment | null | undefined {
    let attachment = this.previousAttachmentMap.get(link)

    if (attachment !== undefined) {
      return attachment
    }

    const normalizedLink = this.normalizeShortUrl(link)
    if (normalizedLink !== link) {
      attachment = this.previousAttachmentMap.get(normalizedLink)
      if (attachment !== undefined) {
        this.previousAttachmentMap.set(link, attachment)
        return attachment
      }
    }

    return undefined
  }

  checkPreviousAttachment(links: string[]) {
    const linksFroUpload: string[] = []
    uniq(links).forEach((link) => {
      if (!this.previousAttachmentMap.has(link)) {
        linksFroUpload.push(link)
        this.previousAttachmentMap.set(link, null)
      }
    })
    if (linksFroUpload.length) {
      AttachmentsApi.preview(linksFroUpload)
        .then(({ data }) => {
          runInAction(() => {
            if (Array.isArray(data)) {
              const unmappedLinks = new Set(linksFroUpload)

              data?.forEach((attachment) => {
                if (attachment) {
                  const attachmentInstance = new Attachment({
                    responseUpload: attachment,
                  })

                  this.previousAttachmentMap.set(attachment.source_short, attachmentInstance)

                  const normalizedSourceShort = this.normalizeShortUrl(attachment.source_short)
                  if (normalizedSourceShort !== attachment.source_short) {
                    this.previousAttachmentMap.set(normalizedSourceShort, attachmentInstance)
                  }

                  unmappedLinks.forEach((originalLink) => {
                    const normalizedOriginal = this.normalizeShortUrl(originalLink)

                    if (normalizedOriginal === normalizedSourceShort) {
                      this.previousAttachmentMap.set(originalLink, attachmentInstance)
                      unmappedLinks.delete(originalLink)
                    }
                  })
                }
              })
            }
          })
        })
        .catch((e) => logger.error(e))
    }
  }

  setPreviousAttachments(attachments: Attachment[]) {
    attachments.forEach((attachment) => {
      if (attachment.source_short && !this.previousAttachmentMap.get(attachment.source_short)) {
        this.previousAttachmentMap.set(attachment.source_short, attachment)
      }
    })
  }

  loadUploadInfo = async () => {
    if (this.loadingUploadInfo) return
    try {
      this.loadingUploadInfo = true
      const { data } = await AttachmentsApi.getUploadInfo()
      Object.entries(data).forEach(([key, info]) => {
        runInAction(() => {
          this.uploadInfoMap.set(key as IUploadInfoKeys, info)
        })
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.loadingUploadInfo = false
      })
    }
  }

  getSizeLimit = (key: IUploadInfoKeys): number => this.uploadInfoMap.get(key)?.size || 0

  getAllowedMimeTypes = (key: IUploadInfoKeys): string[] =>
    this.uploadInfoMap.get(key)?.mime_types || []

  checkFilesSizeLimitsForUpload = async ({
    files,
    setLimitSizeError,
  }: {
    files: File[]
    setLimitSizeError: (limitError: ILimitSizeError, file: File) => void
  }) => {
    if (this.uploadInfoMap.size === 0 && !this.loadingUploadInfo) {
      await this.loadUploadInfo()
    } else if (this.loadingUploadInfo) {
      while (this.loadingUploadInfo) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
    }

    const uploadFiles: File[] = []
    const limitRejectedFilesByType: Record<
      string,
      Array<{ file: File; limitError: ILimitSizeError; index: number }>
    > = {}

    const fileChecks = await Promise.all(
      files.map(async (file, index) => {
        const updatedFile = getFileWithTruncatedName(file)

        const type = await getFileType(file)
        const limit = this.getSizeLimit(type)
        const label = fileTypeToLabelConverter[type]

        if (limit && updatedFile.size > limit * 1024) {
          if (!limitRejectedFilesByType[type]) {
            limitRejectedFilesByType[type] = []
          }
          limitRejectedFilesByType[type].push({
            file: updatedFile,
            limitError: { limit, label },
            index,
          })
          return { shouldUpload: false, file: updatedFile }
        } else {
          return { shouldUpload: true, file: updatedFile }
        }
      })
    )

    const limitRejectedFiles: Record<string, { file: File; limitError: ILimitSizeError }> = {}
    Object.keys(limitRejectedFilesByType).forEach((type) => {
      const sorted = limitRejectedFilesByType[type].sort((a, b) => a.index - b.index)
      limitRejectedFiles[type] = {
        file: sorted[0].file,
        limitError: sorted[0].limitError,
      }
    })

    fileChecks.forEach((check) => {
      if (check.shouldUpload) {
        uploadFiles.push(check.file)
      }
    })

    Object.keys(limitRejectedFiles).forEach((type) => {
      setLimitSizeError(limitRejectedFiles[type].limitError, limitRejectedFiles[type].file)
    })

    return uploadFiles
  }

  addMediaFiles = async ({
    files,
    setLimitSizeError,
    addAttachment,
    setExtError,
  }: {
    files: File[]
    setLimitSizeError: (limitError: ILimitSizeError, file: File) => void
    addAttachment: (attachment: Attachment, update?: boolean) => void
    setExtError: (error: string) => void
  }) => {
    const uploadFiles = await this.checkFilesSizeLimitsForUpload({ files, setLimitSizeError })

    if (uploadFiles.length) {
      const requestPromise = []
      let generalUploadErrorShown = false
      const requestUpload = async (file: File) => {
        try {
          const type = await getContentType(file)
          const item = await AttachmentsApi.uploadLink(file, {
            name: file.name,
            content_type: type,
          })
          const responseAttachment = item.data.attachment

          responseAttachment.source = URL.createObjectURL(file)
          const attachment = new Attachment({
            responseMedia: responseAttachment,
          })
          attachment.setLoading(true)

          addAttachment(attachment)
        } catch (e) {
          await this._reportUploadError(e, setExtError, () => {
            if (!generalUploadErrorShown) {
              generalUploadErrorShown = true
              toastStore.add({
                title: 'Oops! Upload failed. Please try again.',
                type: 'error',
              })
            }
          })
        }
      }

      for (const file of uploadFiles) {
        requestPromise.push(requestUpload(file))
      }

      await Promise.all(requestPromise)
    }
  }

  private _reportUploadError = async (
    e: unknown,
    setExtError: (error: string) => void,
    onGeneralError: () => void
  ) => {
    const { type, error } = await errorHandler<{ [key: string]: string }>(e as Error)

    if (type !== 'axios-error') return

    if (error.response?.data) {
      for (const key in error.response.data) {
        const res = error.response.data[key]

        if (Array.isArray(res)) {
          res.forEach((item) => setExtError(String(item)))
        } else {
          setExtError(String(res))
        }
      }
    } else {
      onGeneralError()
    }
  }

  addMediaFilesFromUrls = async ({
    urls,
    addAttachment,
    setExtError,
  }: {
    urls: string[]
    addAttachment: (attachment: Attachment, update?: boolean) => void
    setExtError: (error: string) => void
  }) => {
    let generalUploadErrorShown = false

    const requestUpload = async (url: string) => {
      try {
        const { data } = await AttachmentsApi.uploadLinkViaUrl(url)

        if (data.attachment) {
          const attachment = new Attachment({
            responseMedia: {
              ...data.attachment,
              name: data.attachment.name || 'attachment',
            },
          })

          addAttachment(attachment)
        }
      } catch (e) {
        await this._reportUploadError(e, setExtError, () => {
          if (!generalUploadErrorShown) {
            generalUploadErrorShown = true
            toastStore.add({
              title: 'Oops! Upload failed. Please try again.',
              type: 'error',
            })
          }
        })
      }
    }

    await Promise.all(urls.map(requestUpload))
  }
}
